import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import {
  type AudienceSegmentEntry,
  type AudienceSegmentSet,
  NormalizedDefinitionOperator,
} from '@openx/types/gqlTypes.ts';
import { convertToPercent } from '@openx/utils/lib/convertToPercent.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealPromptDomainsContext } from '../../utils/context/DealPromptDomainsContext.tsx';
import {
  createNormalizedDefinitionFromSegmentSetAndSelection,
  sanitizeNormalizedDefinitionForPricingQuery,
} from '../../utils/normalizedDefinitionFromAudienceItems.ts';
import type { AudiencePricingResult, NormalizeDefinitionType } from '../../utils/types.ts';
import { FeeSummary } from './FeeSummary.tsx';

type PricingSectionProps = {
  selectedAudienceItems: AudienceSegmentEntry[];
  selectedSegmentSet?: AudienceSegmentSet | null;
};

export const PricingSection = ({ selectedAudienceItems, selectedSegmentSet }: PricingSectionProps) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [pricingData, setPricingData] = useState<AudiencePricingResult['computePricing'] | null>(null);
  const { session } = usePermissionContext();
  const currentAccountId = session?.account.external_id;

  const { useDealPromptPricing, useDealPromptOrganizationsList } = useDealPromptDomainsContext();
  const { fetchDealPromptPricing } = useDealPromptPricing();
  const { organizationsById } = useDealPromptOrganizationsList();

  const getPricingData = useCallback(async () => {
    const rawDefinition = createNormalizedDefinitionFromSegmentSetAndSelection(
      selectedSegmentSet ?? null,
      selectedAudienceItems
    );
    const normalizedDefinition: NormalizeDefinitionType = sanitizeNormalizedDefinitionForPricingQuery(rawDefinition);

    const result = await fetchDealPromptPricing({
      accountId: currentAccountId ?? '',
      normalizedDefinition,
    });

    setPricingData(result.data?.computePricing ?? null);
  }, [fetchDealPromptPricing, selectedAudienceItems, selectedSegmentSet, currentAccountId]);

  useEffect(() => {
    if (selectedAudienceItems.length > 0) {
      getPricingData();
    }
  }, [selectedAudienceItems, getPricingData]);

  const prices = pricingData?.pricing_by_org ?? {};
  const organizationIds = useMemo(() => Object.keys(prices), [prices]);

  return (
    <Box
      data-test="deal-prompt-details-third-party-pricing-root"
      sx={{
        display: 'flex',
        gap: 14,
        width: '100%',
        p: 4,
        paddingBottom: 0,
      }}
    >
      <Box>
        <Typography data-test="deal-prompt-details-third-party-pricing-page-title" variant="h2">
          {t('3rd Party Segments Pricing')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Alert
          data-test="partner-pricing-alert"
          severity="info"
          sx={{
            '& .MuiAlert-message': {
              minWidth: '570px',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              height: 'fit-content',
              gap: 3,
            }}
          >
            <Typography
              data-test="deal-prompt-details-partner-pricing-alert-heading"
              variant="body1Bold"
              component="span"
            >
              {t('Data Partner Pricing')}
            </Typography>
            <Button
              onClick={() => setShow(prev => !prev)}
              aria-expanded={show}
              variant="text"
              color="primary"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {show ? t('Hide details') : t('Show details')}
            </Button>
          </Box>

          <Collapse data-test="partner-pricing-info" in={show} timeout="auto" unmountOnExit>
            {t("You've selected segments from 3P Data Partners - pricing details are outlined below.")}

            {organizationIds.map(orgId => (
              <Box
                key={orgId}
                sx={{
                  mt: 1.5,
                }}
              >
                <Typography data-test="partner-pricing-organization-name" variant="body1Bold" component="span">
                  {organizationsById[orgId]?.name ?? orgId}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <Typography
                    data-test="info-on-alert-percent-of-media"
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('Percent of Media:')}
                  </Typography>

                  <Typography data-test="info-on-alert-percent-of-media-value" variant="body2">
                    {convertToPercent(prices[orgId]?.price_pom ?? 0)}%
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <Typography
                    data-test="info-on-alert-fixed-cpm"
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('Fixed CPM:')}
                  </Typography>
                  <Typography data-test="info-on-alert-fixed-cpm-value" variant="body2">
                    $
                    {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
                      prices[orgId]?.price_cpm ?? 0
                    )}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Collapse>
        </Alert>
        <FeeSummary
          totalFees={pricingData?.totals ?? { price_pom: 0, price_cpm: 0 }}
          operator={pricingData?.operator ?? NormalizedDefinitionOperator.Union}
        />
      </Box>
    </Box>
  );
};
